<template>
  <div
    v-if="!insightLimit"
    class="loading-gradient-large flex h-full w-full flex-col justify-between gap-6 rounded-md border border-yellow-500 p-5"
  >
    <div
      class="flex h-full w-full flex-col items-center justify-center gap-4 rounded-md"
    >
      <div class="h-24">
        <svg
          class="h-full w-full"
          viewBox="0 0 14 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5 5.46094V5.46094C5.80278 7.81176 7.64918 9.65816 10 10.4609V10.4609V10.4609C7.64918 11.2637 5.80278 13.1101 5 15.4609V15.4609V15.4609C4.19722 13.1101 2.35082 11.2637 0 10.4609V10.4609V10.4609C2.35082 9.65816 4.19722 7.81176 5 5.46094V5.46094Z"
            fill="#F8C617"
          />
          <path
            d="M11 0.460938V0.460938C11.4817 1.87143 12.5895 2.97927 14 3.46094V3.46094V3.46094C12.5895 3.9426 11.4817 5.05044 11 6.46094V6.46094V6.46094C10.5183 5.05044 9.41049 3.9426 8 3.46094V3.46094V3.46094C9.41049 2.97927 10.5183 1.87143 11 0.460938V0.460938Z"
            fill="#F8C617"
          />
        </svg>
      </div>
      <div class="text-center text-xl">
        {{ amountOfInsights > 0 ? amountOfInsights + ' ' : '' }}Insights
        Discovered
      </div>
      <div class="text-center text-base text-body-color">
        <div class="text-base">
          You've exceeded the limit of insights you can view.
        </div>
        <div class="mb-4 text-base">Upgrade now to look under the hood.</div>
      </div>
      <a
        class="text-base text-yellow-600 underline"
        target="_blank"
        href="https://calendly.com/bohighway/get-more-out-of-highway"
        >Book a demo</a
      >
    </div>
  </div>
  <div
    v-else
    class="flex h-full w-full flex-col justify-between gap-6 rounded-md border border-yellow-500 bg-yellow-10 p-5"
  >
    <div>
      <div class="flex">
        <div class="flex w-full items-center gap-4">
          <div class="flex items-center justify-start gap-1">
            <svg
              class="h-5 w-5 text-yellow-500"
              viewBox="0 0 14 16"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 5.39062C5.80277 7.74145 7.64918 9.58785 10 10.3906C7.64918 11.1934 5.80277 13.0398 5 15.3906C4.19722 13.0398 2.35082 11.1934 0 10.3906C2.35082 9.58785 4.19723 7.74145 5 5.39062Z"
                fill="currentColor"
              />
              <path
                d="M11 0.390625C11.4817 1.80112 12.5895 2.90896 14 3.39062C12.5895 3.87229 11.4817 4.98013 11 6.39062C10.5183 4.98013 9.41049 3.87229 8 3.39062C9.41049 2.90896 10.5183 1.80112 11 0.390625Z"
                fill="currentColor"
              />
            </svg>
            <h3 class="text-base text-dark-gray lg:text-xl">Insights</h3>
          </div>
          <div class="flex items-center">
            <custom-badge
              v-if="!loading"
              bg-color-class="bg-yellow-500"
              color-class="text-white"
              :size="totalInsightSize"
              :text="`${insights.length} ${totalInsightText}`"
            />
            <div
              v-else
              class="loading-yellow-badge h-6 w-36"
            />
          </div>
        </div>
        <div
          v-if="activeEld"
          class="h-8 w-8 cursor-pointer rounded-full bg-yellow-200 p-1 hover:bg-yellow-300"
          @click="handleCollapse()"
        >
          <svg
            v-if="collapsed"
            class="h-6 w-6 text-yellow-500"
            data-option="dropdown"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            style="transform: rotate(180deg)"
          >
            <path
              data-option="dropdown"
              d="M6.34292 7.75734L4.92871 9.17155L11.9998 16.2426L19.0708 9.17158L17.6566 7.75737L11.9998 13.4142L6.34292 7.75734Z"
            />
          </svg>
          <svg
            v-else
            class="h-6 w-6 text-yellow-500"
            data-option="dropdown"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            style="margin-top: 1.5px"
          >
            <path
              data-option="dropdown"
              d="M6.34292 7.75734L4.92871 9.17155L11.9998 16.2426L19.0708 9.17158L17.6566 7.75737L11.9998 13.4142L6.34292 7.75734Z"
            />
          </svg>
        </div>
      </div>
      <div v-if="collapsed || !activeEld">
        <div
          v-if="!loading"
          class="flex-start mt-8 flex flex-col gap-4 text-left"
        >
          <div
            v-for="(key, index) in categoryKeys"
            :key="index"
          >
            <h4 class="text-xs font-bold uppercase text-dark-gray">
              {{ key }} Insights
            </h4>
            <div class="mt-3">
              <custom-badge
                v-for="(insight, index) in selectedInsightTypes(key)"
                :key="`data-insight-badge-${index}`"
                bg-color-class="bg-white"
                color-class="text-dark-gray"
                size="md"
                icon-size="lg"
                :bind="insight.id"
                :text="
                  screenWidth < 376
                    ? formatInsightTitle(insight.title)
                    : insight.title
                "
                :icon="iconName(insight.is_type)"
                :icon-color-class="iconColorClass(insight.is_type)"
                class="mb-2 mr-3 inline-flex self-start border border-light-gray text-xs"
              />
            </div>
          </div>
        </div>
        <div
          v-else
          class="flex-start mt-8 flex flex-col gap-4"
        >
          <div class="flex flex-col items-start gap-2">
            <div>
              <div class="loading-yellow h-4 w-40" />
            </div>
            <div class="loading-yellow h-10 w-full" />
          </div>
          <div class="flex flex-col items-start gap-2">
            <div>
              <div class="loading-yellow h-4 w-40" />
            </div>
            <div class="loading-yellow h-10 w-full" />
          </div>
          <div class="flex flex-col items-start gap-2">
            <div>
              <div class="loading-yellow h-4 w-40" />
            </div>
            <div class="loading-yellow h-10 w-full" />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="collapsed"
      class="text-3xs text-gray-over-dark"
    >
      Insights discovered from government data and internal database.
    </div>
  </div>
</template>
<script>
import CustomBadge from '@/components/CustomBadge.vue';

export default {
  name: 'InsightsSection',
  components: {
    CustomBadge,
  },
  props: {
    insights: {
      type: Object,
      required: true,
    },
    screenWidth: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      collapsed: false,
      categories: {
        Location: [
          'broker_authority_nearby',
          'undeliverable_address',
          'residential_address',
          'virtual_office_address',
          'broker_authority_same_address',
          'only_accessed_from_outside_north_america',
        ],
        Operation: [
          'no_inspection_history',
          'low_inspection_to_truck_count',
          'authority_history_gap',
          'no_class_eight_trucks',
          'reused_equipment_same_license_plate',
          'equipment_not_listed_on_scheduled_auto',
          'fatal_crash_with_citation',
          'acute_or_critical_sms_violations',
          'material_reported_vs_scheduled_equipment_contradiction',
          'fmcsa_phone_number_changed_recently',
          'fmcsa_email_changed_recently',
        ],
        Association: [
          'reused_email_address',
          'reused_phone',
          'reused_contact',
          'reused_address',
          'carrier_identifier_changed',
          'associated_digital_activity',
          'flagged_factor',
          'flagged_insurance_agent',
          'reused_equipment_scheduled_auto',
          'census_contact_has_suspicious_voip_phone_number',
          'highly_used_address_area',
          'user_attempting_to_login_as_wrong_carrier',
        ],
      },
    };
  },
  computed: {
    totalInsightText() {
      return this.screenWidth < 400
        ? 'Total Insights'
        : 'Total Insights Discovered';
    },
    totalInsightSize() {
      return this.screenWidth < 400 ? 'md' : 'lg';
    },
    amountOfInsights() {
      return (
        this.$store.state.carriers?.showCarrierInsights?.insights?.length || 0
      );
    },
    loading() {
      //   return this.$store.state.loading.isLoading;
      return false;
    },
    activeEld() {
      // const canConnect = this.$store.state.users.currentUser?.paywall?.can_connect;
      // const canRule = this.$store.state.users.currentUser?.paywall?.can_rule;
      // const existChannelName = this.$store.state.carriers.showCarrier.active_eld_connect_integration?.channel_name?.length > 0;

      // return existChannelName && canConnect || existChannelName && canRule;
      return false;
    },
    categoryKeys() {
      return Object.keys(this.categories).filter(
        (key) => this.selectedInsightTypes(key).length > 0
      );
    },
    insight_usage() {
      return 0;
    },
    insightLimit() {
      // return this.$store.state.users.currentUser?.paywall?.can_view_insights ;
      return true;
    },
  },
  beforeMount() {
    this.$emit('collapsed', this.collapsed);
  },
  methods: {
    formatInsightTitle(title) {
      if (title == 'Only Accessed From Outside North America') {
        return 'Only Accessed From Outside NA';
      }
      if (title == 'Census Contact Has Suspicious Voip Phone Number') {
        return `${title.slice(0, 30)}...`;
      }

      return title;
    },
    handleCollapse() {
      this.collapsed = !this.collapsed;
      this.$emit('collapsed', this.collapsed);
    },
    selectedInsightTypes(key) {
      const is_types = this.categories[key];
      return this.filterInsights(is_types);
    },
    isInsightPresent(insightType) {
      return this.insights.filter((insight) => {
        return insightType == insight.is_type ? insight : null;
      })[0];
    },
    filterInsights(is_types) {
      return this.insights.filter((insight) => {
        return is_types.includes(insight.is_type) ? insight : null;
      });
    },
    iconColorClass: function (insightType) {
      switch (insightType) {
        case 'undeliverable_address':
          return 'text-dark-gray';
        case 'residential_address':
          return 'text-dark-gray';
        case 'no_class_eight_trucks':
          return 'text-dark-gray';
        case 'fatal_crash_with_citation':
          return 'text-red-500';
        // case 'Success':
        //     return 'text-green-500';
        // case 'Warning':
        //     return 'text-orange-500';
        // case 'Info':
        //     return 'text-dark-gray';
        default:
          return 'text-orange-500';
      }
    },
    iconName: function (insightType) {
      switch (insightType) {
        case 'undeliverable_address':
          return 'question';
        case 'residential_address':
          return 'question';
        case 'no_class_eight_trucks':
          return 'question';
        // case 'Success':
        //     return 'checkmark';
        // case 'Warning':
        //     return 'exclamation';
        // case 'Info':
        //     return 'question';
        default:
          return 'exclamation';
      }
    },
  },
};
</script>
